import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import { guardarContratoEnNasPreferido, rutaCarpetaDesdeNumero, sincronizarDocumentosNasLocal } from "../../utils/abrirRutaWindows";
import { formatEuroConSimbolo, parseEuroAmount } from "../../utils/moneyParse";

const TIPOS_CONTRATO = [{ value: "normal", label: "Normal" }];
const FORMATOS_CONTRATO = [
  { value: "word", label: "Word" },
  { value: "pdf", label: "PDF" },
];

const CLAUSULA_VACIA = () => ({ borrador: "", final: "", redactando: false });

const SECTION_ACCENTS = {
  config: { dark: "#60a5fa", light: "#2563eb" },
  pago: { dark: "#a5b4fc", light: "#6366f1" },
  clausulas: { dark: "#c4b5fd", light: "#7c3aed" },
};

export default function ModalConfeccionContrato({
  isOpen, onClose, onGenerado, expedienteId, empresa = "Vitalcasa",
  getDatosPantalla = null,
  carpetaNasFallback = "", numeroExpediente = "",
  puedeModificar = true,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#f1f5f9" : c.textPrimary;
  const txMuted = isDark ? "#94a3b8" : c.textMuted;
  const txHint = isDark ? "#cbd5e1" : "#475569";

  const accent = (key) => (isDark ? SECTION_ACCENTS[key].dark : SECTION_ACCENTS[key].light);

  const [tipo, setTipo] = useState("normal");
  const [idioma, setIdioma] = useState("español");
  const [formato, setFormato] = useState("word");
  const [plazo, setPlazo] = useState("90");
  const [clausulas, setClausulas] = useState([CLAUSULA_VACIA()]);
  const [segundoPago, setSegundoPago] = useState("");
  const [segundoPagoFecha, setSegundoPagoFecha] = useState("");
  const [idiomas, setIdiomas] = useState(["español"]);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState("");

  const sugerirSegundoPago = useCallback(() => {
    if (typeof getDatosPantalla !== "function") return { importe: "", fecha: "" };
    const p = getDatosPantalla();
    const senales = Array.isArray(p?.senales) ? p.senales : [];
    let total = 0;
    let fecha = "";
    for (let i = 1; i < senales.length; i++) {
      const s = senales[i];
      total += (Number(s.importe_comprador) || 0) + (Number(s.importe_agencia) || 0) + (Number(s.importe_propietario) || 0);
      if (!fecha && s.fecha) fecha = String(s.fecha).slice(0, 10);
    }
    return {
      importe: total > 0 ? formatEuroConSimbolo(total) : "",
      fecha,
    };
  }, [getDatosPantalla]);

  useEffect(() => {
    if (!isOpen) return;
    setTipo("normal");
    setIdioma("español");
    setFormato("word");
    setPlazo("90");
    setClausulas([CLAUSULA_VACIA()]);
    const sugerido = sugerirSegundoPago();
    setSegundoPago(sugerido.importe);
    setSegundoPagoFecha(sugerido.fecha);
    setError("");
    api.get("/expedientes/idiomas-agencia")
      .then((r) => {
        const lista = r.data?.data;
        if (Array.isArray(lista) && lista.length) setIdiomas(lista);
      })
      .catch(() => {});
  }, [isOpen, sugerirSegundoPago]);

  if (!isOpen) return null;

  const inp = {
    padding: "7px 10px", borderRadius: 6, fontSize: 12, width: "100%",
    background: isDark ? "#0d1117" : "#ffffff",
    border: `1px solid ${isDark ? "#4a6080" : c.inputBorder}`,
    color: tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  };
  const lbl = { fontSize: 10, fontWeight: 700, color: txHint, marginBottom: 4, display: "block", letterSpacing: "0.02em" };
  const btn = {
    padding: "8px 16px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer",
    border: "none", fontFamily: "inherit",
  };

  const sectionBox = (key) => {
    const col = accent(key);
    return {
      background: isDark ? `${col}12` : `${col}0a`,
      border: `1px solid ${isDark ? `${col}44` : `${col}35`}`,
      borderLeft: `4px solid ${col}`,
      borderRadius: 8,
      padding: "12px 14px",
    };
  };
  const sectionTitle = (key, title) => {
    const col = accent(key);
    return {
      fontSize: 11,
      fontWeight: 800,
      color: col,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 10,
      paddingBottom: 6,
      borderBottom: `1px solid ${col}33`,
    };
  };
  const hint = { fontSize: 10, color: txMuted, marginTop: 4, lineHeight: 1.45 };

  const contextoPantalla = () => {
    const pantalla = typeof getDatosPantalla === "function" ? getDatosPantalla() : null;
    return pantalla && typeof pantalla === "object" ? pantalla : {};
  };

  const actualizarClausula = (i, patch) => {
    setClausulas((prev) => prev.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  };

  const redactarClausula = async (i) => {
    if (!expedienteId) {
      setError("Guarda el expediente antes de redactar cláusulas.");
      return;
    }
    const borrador = String(clausulas[i]?.borrador || "").trim();
    if (!borrador) {
      setError("Escribe primero el borrador de la cláusula.");
      return;
    }
    setError("");
    actualizarClausula(i, { redactando: true });
    try {
      const r = await api.post(`/expedientes/${expedienteId}/redactar-clausula-arras`, {
        borrador,
        idioma,
        plazo: Number(plazo) || 90,
        ...contextoPantalla(),
      });
      const texto = r.data?.data?.texto || "";
      actualizarClausula(i, { final: texto, redactando: false });
    } catch (e) {
      actualizarClausula(i, { redactando: false });
      setError(e.response?.data?.error || e.message || "Error al redactar la cláusula");
    }
  };

  const redactarTodas = async () => {
    for (let i = 0; i < clausulas.length; i++) {
      if (!String(clausulas[i]?.borrador || "").trim()) continue;
      // eslint-disable-next-line no-await-in-loop
      await redactarClausula(i);
    }
  };

  const generar = async () => {
    if (!expedienteId) {
      setError("Guarda el expediente antes de generar el contrato.");
      return;
    }
    const clausulasEnvio = clausulas.map((c) => String(c.final || "").trim()).filter(Boolean);
    const pendientes = clausulas.some((c) => String(c.borrador || "").trim() && !String(c.final || "").trim());
    if (pendientes) {
      setError("Hay cláusulas con borrador sin redactar. Pulsa «Redactar con IA» o completa la cláusula final.");
      return;
    }
    setGenerando(true);
    setError("");
    try {
      const pantalla = contextoPantalla();
      const segundo = parseEuroAmount(segundoPago);
      const r = await api.post(
        `/expedientes/${expedienteId}/generar-contrato-arras`,
        {
          tipo,
          idioma,
          formato,
          plazo: Number(plazo) || 90,
          clausulas_especiales: clausulasEnvio,
          ...(segundo != null ? { segundo_pago: segundo } : {}),
          ...(segundoPagoFecha ? { segundo_pago_fecha: segundoPagoFecha } : {}),
          ...(Object.keys(pantalla).length ? pantalla : {}),
        },
        { timeout: 180000 },
      );

      const data = r.data?.data || {};
      const nombre = data.nombreArchivo || "Contrato arras.docx";
      let carpetaNas = data.carpetaNas || data.carpetaNasActualizada || carpetaNasFallback || "";
      if (!carpetaNas && numeroExpediente) {
        carpetaNas = rutaCarpetaDesdeNumero(numeroExpediente);
      }

      let guardadoFinal = !!data.guardadoNas;
      let rutaNas = data.rutaNas || "";
      let errorNas = data.errorNas || "";
      let needsReinstall = false;

      if (!guardadoFinal && data.archivoBase64) {
        const local = await guardarContratoEnNasPreferido({
          carpeta: carpetaNas,
          nombre,
          base64: data.archivoBase64,
        });
        guardadoFinal = local.guardadoNas;
        rutaNas = local.rutaNas || rutaNas;
        if (!guardadoFinal) errorNas = local.errorNas || errorNas;
        needsReinstall = local.needsReinstall;
      }

      let docsSync = null;
      if (guardadoFinal && carpetaNas && data.documentosNas?.length) {
        docsSync = await sincronizarDocumentosNasLocal(carpetaNas, data.documentosNas);
      }

      onGenerado?.({
        nombre,
        rutaNas,
        carpetaNas,
        guardadoNas: guardadoFinal,
        empresa,
        errorNas,
        needsReinstall,
        docsSync,
        carpetaNasActualizada: data.carpetaNasActualizada || null,
        plazo_contrato: data.plazo_contrato,
        fecha_fin_contrato: data.fecha_fin_contrato,
      });
      onClose();
    } catch (e) {
      let msg = e.response?.data?.error || e.message || "Error al generar el contrato";
      setError(msg);
    } finally {
      setGenerando(false);
    }
  };

  const overlay = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  };
  const panel = {
    background: isDark ? "#141820" : "#f8f9fb",
    border: `1px solid ${isDark ? "#3d4a5c" : c.cardBorder}`,
    borderRadius: 12, width: "100%", maxWidth: 660,
    maxHeight: "92vh", overflowY: "auto",
    boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
  };
  const headerBar = {
    background: isDark
      ? "linear-gradient(135deg, #1e3a5f 0%, #1a2744 100%)"
      : "linear-gradient(135deg, #1a3a5c 0%, #0f2540 100%)",
    padding: "16px 20px",
    borderRadius: "12px 12px 0 0",
    borderBottom: `3px solid ${isDark ? "#60a5fa" : "#3b82f6"}`,
  };
  const body = { padding: "16px 20px", display: "grid", gap: 14 };
  const btnSecondary = {
    ...btn,
    background: isDark ? "#2a3344" : "#e2e8f0",
    color: tx,
    border: `1px solid ${isDark ? "#4a5568" : "#cbd5e1"}`,
  };
  const btnPrimary = {
    ...btn,
    background: isDark ? "#2563eb" : "#1a3a5c",
    color: "#ffffff",
    boxShadow: isDark ? "0 2px 8px rgba(37,99,235,0.35)" : "0 2px 8px rgba(26,58,92,0.25)",
  };
  const btnIA = {
    padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
    border: "none", fontFamily: "inherit",
    background: isDark ? "#6d28d9" : "#7c3aed",
    color: "#ffffff",
  };
  const btnMini = {
    padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer",
    border: `1px solid ${isDark ? "#4a5568" : "#cbd5e1"}`,
    background: isDark ? "#2a3344" : "#ffffff",
    color: tx, fontFamily: "inherit",
  };
  const formatoLabel = FORMATOS_CONTRATO.find((f) => f.value === formato)?.label || "Word";
  const algunaRedactando = clausulas.some((c) => c.redactando);
  const cerrandoBloqueado = generando || algunaRedactando;
  const bloqueado = cerrandoBloqueado || !puedeModificar;
  const clausulasAccent = accent("clausulas");

  return createPortal(
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !cerrandoBloqueado && onClose()}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={headerBar}>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#ffffff", marginBottom: 4 }}>
            Contrato Arras
          </div>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.82)", margin: 0, lineHeight: 1.45 }}>
            Empresa: <strong style={{ color: "#fff" }}>{empresa}</strong>
            {" · "}
            El archivo ({formatoLabel}) se guardará en la carpeta NAS del expediente.
          </p>
        </div>

        <div style={body}>
          {/* —— Configuración —— */}
          <div style={sectionBox("config")}>
            <div style={sectionTitle("config", "Configuración")}>Configuración del contrato</div>
            <div style={{ display: "grid", gridTemplateColumns: "1.05fr 0.82fr 1fr 0.82fr", gap: 8 }}>
              <div>
                <label style={lbl}>Tipo de contrato</label>
                <select style={inp} value={tipo} onChange={(e) => setTipo(e.target.value)} disabled={bloqueado}>
                  {TIPOS_CONTRATO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Plazo (días)</label>
                <input
                  type="number"
                  min={1}
                  max={999}
                  style={inp}
                  value={plazo}
                  onChange={(e) => setPlazo(e.target.value)}
                  disabled={bloqueado}
                />
              </div>
              <div>
                <label style={lbl}>Idioma</label>
                <select style={inp} value={idioma} onChange={(e) => setIdioma(e.target.value)} disabled={bloqueado}>
                  {idiomas.map((id) => <option key={id} value={id}>{id}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Formato</label>
                <select style={inp} value={formato} onChange={(e) => setFormato(e.target.value)} disabled={bloqueado}>
                  {FORMATOS_CONTRATO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
            {idioma && !/^espa/i.test(idioma) && (
              <div style={{
                fontSize: 10, marginTop: 8, padding: "6px 10px", borderRadius: 6,
                background: isDark ? "rgba(239,68,68,0.12)" : "#fef2f2",
                border: `1px solid ${isDark ? "rgba(239,68,68,0.35)" : "#fecaca"}`,
                color: isDark ? "#fca5a5" : "#b91c1c",
              }}>
                Contrato bilingüe: español en negro y {idioma} en rojo en el documento.
              </div>
            )}
          </div>

          {/* —— Segundo pago —— */}
          <div style={sectionBox("pago")}>
            <div style={sectionTitle("pago", "Pago")}>Segundo pago · Pactos Primero, apartado 2</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 150px", gap: 10, alignItems: "end" }}>
              <div>
                <label style={lbl}>Importe</label>
                <input
                  style={inp}
                  value={segundoPago}
                  onChange={(e) => setSegundoPago(e.target.value)}
                  onBlur={(e) => setSegundoPago(formatEuroConSimbolo(e.target.value))}
                  placeholder="0,00 €"
                  disabled={bloqueado}
                />
              </div>
              <div>
                <label style={lbl}>Fecha</label>
                <input
                  type="date"
                  style={inp}
                  value={segundoPagoFecha}
                  onChange={(e) => setSegundoPagoFecha(e.target.value)}
                  disabled={bloqueado}
                />
              </div>
            </div>
            <p style={hint}>
              El apartado 3 calculará el resto: precio menos señales y este importe.
            </p>
          </div>

          {/* —— Cláusulas —— */}
          <div style={sectionBox("clausulas")}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
              <div style={{ ...sectionTitle("clausulas", "Cláusulas"), marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>
                Cláusulas especiales
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button
                  type="button"
                  style={btnMini}
                  onClick={() => setClausulas((prev) => [...prev, CLAUSULA_VACIA()])}
                  disabled={bloqueado}
                >
                  + Nueva cláusula
                </button>
                <button
                  type="button"
                  style={btnIA}
                  onClick={redactarTodas}
                  disabled={bloqueado}
                >
                  Redactar todas con IA
                </button>
              </div>
            </div>
            <p style={{ ...hint, marginTop: 0, marginBottom: 10 }}>
              Escribe tu intención en el borrador; la IA redactará una cláusula breve (2-4 frases) adaptada al contrato, sin repetir dirección ni datos de las partes. Puedes retocarla antes de generar el Word.
            </p>
            <div style={{ display: "grid", gap: 10 }}>
              {clausulas.map((clausula, i) => (
                <div
                  key={i}
                  style={{
                    border: `1px solid ${isDark ? `${clausulasAccent}33` : `${clausulasAccent}28`}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    background: isDark ? "#0d1117" : "#ffffff",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: clausulasAccent, letterSpacing: "0.04em" }}>
                      Cláusula {i + 1}
                    </span>
                    {clausulas.length > 1 && (
                      <button
                        type="button"
                        style={{
                          ...btnMini, padding: "2px 8px",
                          color: "#ffffff", background: "#dc2626", border: "none",
                        }}
                        onClick={() => setClausulas((prev) => prev.filter((_, j) => j !== i))}
                        disabled={bloqueado}
                        title="Quitar cláusula"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <label style={lbl}>Borrador (tu intención)</label>
                  <textarea
                    style={{ ...inp, minHeight: 56, resize: "vertical", marginBottom: 8 }}
                    value={clausula.borrador}
                    onChange={(e) => actualizarClausula(i, { borrador: e.target.value })}
                    placeholder="Ej.: el comprador se queda la cocina equipada y el vendedor se lleva la nevera del garaje..."
                    disabled={bloqueado}
                  />
                  <button
                    type="button"
                    style={{ ...btnIA, marginBottom: 8 }}
                    onClick={() => redactarClausula(i)}
                    disabled={bloqueado || !String(clausula.borrador || "").trim()}
                  >
                    {clausula.redactando ? "Redactando…" : "Redactar con IA"}
                  </button>
                  <label style={lbl}>Cláusula final (para el contrato)</label>
                  <textarea
                    style={{ ...inp, minHeight: 72, resize: "vertical" }}
                    value={clausula.final}
                    onChange={(e) => actualizarClausula(i, { final: e.target.value })}
                    placeholder="Texto jurídico que se insertará en el contrato…"
                    disabled={bloqueado}
                  />
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div style={{
              fontSize: 11, color: isDark ? "#fca5a5" : "#b91c1c",
              padding: "8px 12px", borderRadius: 6,
              background: isDark ? "rgba(239,68,68,0.12)" : "#fef2f2",
              border: `1px solid ${isDark ? "rgba(239,68,68,0.35)" : "#fecaca"}`,
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={{
          display: "flex", justifyContent: "flex-end", gap: 10,
          padding: "14px 20px",
          borderTop: `1px solid ${isDark ? "#2a3344" : "#e2e8f0"}`,
          background: isDark ? "#101318" : "#f1f5f9",
          borderRadius: "0 0 12px 12px",
        }}>
          <button type="button" style={btnSecondary} onClick={onClose} disabled={cerrandoBloqueado}>
            Cancelar
          </button>
          <button
            type="button"
            style={btnPrimary}
            onClick={generar}
            disabled={bloqueado}
            title={!puedeModificar ? "No tienes permiso para generar este contrato" : undefined}
          >
            {generando ? "Generando…" : `Generar ${formatoLabel}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
